(() => {
  const MODULE_ID = "ig-payment-options";
  const SHADOW_STYLE_ID = "ig-unified-payment-font";
  const ROOT_SELECTOR = "product-info.product__info";

  let observer = null;
  let syncTimer = null;

  const normalizeText = (text = "") =>
    text.replace(/\s+/g, " ").trim();

  const getText = (element) =>
    normalizeText(
      element?.innerText ||
      element?.textContent ||
      ""
    );

  const getShadowText = (element) =>
    normalizeText(
      element?.shadowRoot?.textContent ||
      ""
    );

  const getRoot = () =>
    document.querySelector(ROOT_SELECTOR);

  /*
   * =========================================================
   * FIND PAYMENT WIDGETS
   * =========================================================
   */

  const findExternalShopPayForms = (root) =>
    [
      ...(root?.querySelectorAll(
        ".product__price form.installment"
      ) || [])
    ];

  const findModuleShopPayForm = () =>
    document.querySelector(
      `#${MODULE_ID} form.installment`
    );

  const findAfterpayBlock = (root) => {
    const placement = root?.querySelector(
      'square-placement[id*="afterpay"]'
    );

    return placement?.closest(
      ".shopify-block.shopify-app-block"
    ) || null;
  };

  /*
   * Synchrony puede aparecer primero como
   * #syf-unifi-widget o como #syf-promo.
   * Buscamos cualquiera de los dos.
   */
  const findSynchronyBlock = (root) => {
    const widget =
      root?.querySelector("#syf-unifi-widget") ||
      root?.querySelector("#syf-promo");

    return widget?.closest(
      ".shopify-block.shopify-app-block"
    ) || null;
  };

  const findSynchronyInfo = (root) =>
    [
      ...(root?.querySelectorAll(
        ".product__text"
      ) || [])
    ].find((element) =>
      /synchrony financing available/i.test(
        getText(element)
      )
    ) || null;

  /*
   * =========================================================
   * AVAILABILITY
   * =========================================================
   */

  const isShopPayAvailable = (form) => {
    const paymentTerms = form?.querySelector(
      "shopify-payment-terms"
    );

    if (!form || !paymentTerms) {
      return false;
    }

    /*
     * Primera validación: metadata oficial de Shopify.
     */
    const metaAttribute =
      paymentTerms.getAttribute("shopify-meta");

    if (metaAttribute) {
      try {
        const meta = JSON.parse(metaAttribute);

        const eligibleVariant =
          Array.isArray(meta?.variants) &&
          meta.variants.some(
            (variant) =>
              variant.eligible === true &&
              variant.available === true
          );

        if (eligibleVariant) {
          return true;
        }
      } catch (error) {
        /*
         * Si Shopify cambia el formato,
         * seguimos con la validación visual.
         */
      }
    }

    /*
     * Segunda validación: contenido ya renderizado.
     */
    const content = [
      getText(form),
      getShadowText(paymentTerms)
    ].join(" ");

    return /installment|sample plans|shop pay|\/mo|interest-free/i.test(
      content
    );
  };

  const isAfterpayAvailable = (block) => {
    const placement = block?.querySelector(
      'square-placement[id*="afterpay"]'
    );

    const content = [
      getText(block),
      getShadowText(placement)
    ].join(" ");

    return !!placement &&
      /afterpay|interest-free payments?|\/mo/i.test(
        content
      );
  };

  /*
   * Para Synchrony NO dependemos más del texto interno
   * o del Shadow DOM para decidir disponibilidad.
   *
   * Si existe su widget o promo, lo consideramos
   * disponible. Esto evita que desaparezca mientras
   * todavía está terminando de cargar.
   */
  const isSynchronyAvailable = (block) => {
    if (!block) {
      return false;
    }

    return !!(
      block.querySelector("#syf-unifi-widget") ||
      block.querySelector("#syf-promo")
    );
  };

  /*
   * =========================================================
   * SHADOW DOM FONT STYLES
   * =========================================================
   */

  const injectShadowStyle = (
    shadowRoot,
    css
  ) => {
    if (!shadowRoot) return;

    let style = shadowRoot.getElementById(
      SHADOW_STYLE_ID
    );

    if (!style) {
      style = document.createElement("style");
      style.id = SHADOW_STYLE_ID;
      shadowRoot.appendChild(style);
    }

    style.textContent = css;
  };

  const styleShopPay = (form) => {
    const paymentTerms = form?.querySelector(
      "shopify-payment-terms"
    );

    injectShadowStyle(
      paymentTerms?.shadowRoot,
      `
        :host,
        p,
        span,
        a,
        button,
        strong,
        em {
          font-family: Poppins, sans-serif !important;
          font-size: 12px !important;
          line-height: 1.45 !important;
        }

        p {
          margin: 0 !important;
        }

        #shopify-installments-content {
          padding: 0 5px 0 0 !important;
        }

        #shopify-installments-cta {
          font-family: Poppins, sans-serif !important;
          font-size: 12px !important;
          line-height: 1.45 !important;
        }
      `
    );
  };

  const styleAfterpay = (block) => {
    const placement = block?.querySelector(
      'square-placement[id*="afterpay"]'
    );

    if (!placement) return;

    placement.style.setProperty(
      "--messaging-margin-block-start",
      "0px"
    );

    placement.style.setProperty(
      "--messaging-margin-block-end",
      "0px"
    );

    placement.style.setProperty(
      "--messaging-font-size",
      "12px"
    );

    placement.style.setProperty(
      "--messaging-line-height",
      "1.45"
    );

    placement.style.setProperty(
      "--logo-badge-width",
      "78px"
    );

    injectShadowStyle(
      placement.shadowRoot,
      `
        :host,
        .afterpay-paragraph,
        .afterpay-paragraph span,
        .afterpay-paragraph strong,
        .afterpay-paragraph button,
        .afterpay-paragraph a {
          font-family: Poppins, sans-serif !important;
          font-size: 12px !important;
          line-height: 1.45 !important;
        }

        :host {
          margin-block-start: 0 !important;
          margin-block-end: 0 !important;
        }

        .afterpay-paragraph {
          margin: 0 !important;
        }
      `
    );
  };

  const styleSynchrony = (block) => {
    const applyStyles = () => {
      const promo =
        block?.querySelector("#syf-promo") ||
        document.querySelector(
          `#${MODULE_ID} #syf-promo`
        ) ||
        document.querySelector("#syf-promo");

      if (!promo?.shadowRoot) {
        return false;
      }

      injectShadowStyle(
        promo.shadowRoot,
        `
          :host,
          .widget-box,
          .widget-box div,
          .widget-box p,
          .widget-box span,
          .widget-box a,
          .widget-box button,
          .widget-box strong,
          .widget-box em {
            font-family: Poppins, sans-serif !important;
            font-size: 12px !important;
            line-height: 1.35 !important;
          }

          .widget-box {
            min-height: 0 !important;
            padding-top: 2px !important;
            padding-bottom: 2px !important;
            align-items: center !important;
            background: #ffffff !important;
          }

          img#Revolving {
            width: 38px !important;
            min-width: 38px !important;
            max-width: 38px !important;
            height: auto !important;
            flex: 0 0 38px !important;
          }
        `
      );

      return true;
    };

    if (applyStyles()) return;

    /*
     * Synchrony puede crear su Shadow DOM unos segundos
     * después de que aparece el bloque.
     *
     * Reintentamos durante aproximadamente 5 segundos.
     */
    let attempts = 0;

    const interval = window.setInterval(() => {
      attempts += 1;

      if (applyStyles() || attempts >= 20) {
        window.clearInterval(interval);
      }
    }, 250);
  };

  /*
   * =========================================================
   * PAYMENT MODULE
   * =========================================================
   */

  const ensureModule = (root) => {
    let module = document.getElementById(
      MODULE_ID
    );

    if (module) return module;

    const priceBlock = root?.querySelector(
      ".product__price"
    );

    if (!priceBlock) return null;

    module = document.createElement("div");
    module.id = MODULE_ID;
    module.className = "ig-payment-options";

    priceBlock.insertAdjacentElement(
      "afterend",
      module
    );

    return module;
  };

  const createSlot = (key) => {
    const slot = document.createElement("div");

    slot.className = "ig-payment-option";
    slot.dataset.paymentMethod = key;

    return slot;
  };

  const getSlot = (module, key) => {
    let slot = module.querySelector(
      `[data-payment-method="${key}"]`
    );

    if (!slot) {
      slot = createSlot(key);
      module.appendChild(slot);
    }

    return slot;
  };

  const removeSlot = (module, key) => {
    module
      .querySelector(
        `[data-payment-method="${key}"]`
      )
      ?.remove();
  };

  /*
   * =========================================================
   * SHOP PAY
   * =========================================================
   */

  const updateShopPay = (
    root,
    module
  ) => {
    const externalForms =
      findExternalShopPayForms(root);

    const currentForm =
      findModuleShopPayForm();

    /*
     * Shopify puede crear un nuevo form al cambiar
     * variante. Siempre priorizamos el último form
     * generado dentro del bloque de precio.
     */
    const newestExternalForm =
      externalForms.at(-1);

    const candidate =
      newestExternalForm || currentForm;

    if (!isShopPayAvailable(candidate)) {
      removeSlot(module, "shop-pay");

      externalForms.forEach((form) => {
        if (!isShopPayAvailable(form)) {
          form.remove();
        }
      });

      return;
    }

    const slot = getSlot(
      module,
      "shop-pay"
    );

    if (
      candidate &&
      candidate.parentElement !== slot
    ) {
      slot.replaceChildren(candidate);
    }

    /*
     * Elimina cualquier duplicado que Shopify
     * haya dejado fuera del módulo.
     */
    findExternalShopPayForms(root)
      .filter((form) => form !== candidate)
      .forEach((form) => form.remove());

    styleShopPay(candidate);
  };

  /*
   * =========================================================
   * AFTERPAY
   * =========================================================
   */

  const updateAfterpay = (
    root,
    module
  ) => {
    const block = findAfterpayBlock(root);

    if (!isAfterpayAvailable(block)) {
      removeSlot(module, "afterpay");
      return;
    }

    const slot = getSlot(
      module,
      "afterpay"
    );

    if (block.parentElement !== slot) {
      slot.replaceChildren(block);
    }

    styleAfterpay(block);
  };

  /*
   * =========================================================
   * SYNCHRONY
   * =========================================================
   */

  const updateSynchrony = (
    root,
    module
  ) => {
    const block = findSynchronyBlock(root);
    const info = findSynchronyInfo(root);

    /*
     * IMPORTANT:
     *
     * Synchrony puede tardar bastante en aparecer,
     * especialmente en mobile.
     *
     * Si todavía no encontramos el bloque,
     * NO escondemos el texto y NO escondemos el slot.
     *
     * Simplemente esperamos al próximo sync.
     */
    if (!block) {
      return;
    }

    /*
     * El bloque apareció pero Synchrony todavía puede
     * estar inicializando su widget interno.
     *
     * De nuevo: no escondemos nada.
     */
    if (!isSynchronyAvailable(block)) {
      return;
    }

    const slot = getSlot(
      module,
      "synchrony"
    );

    /*
     * Si anteriormente había quedado hidden por
     * una ejecución vieja, lo restauramos.
     */
    slot.hidden = false;

    if (info) {
      info.hidden = false;
    }

    const content = [];

    /*
     * Primero:
     * "Synchrony financing available..."
     */
    if (info) {
      content.push(info);
    }

    /*
     * Después:
     * "Pay with Synchrony Luxury Credit Card..."
     */
    content.push(block);

    /*
     * Solo modificamos el DOM si realmente
     * hace falta. Esto evita re-renders innecesarios
     * de Synchrony.
     */
    const correctContent =
      content.every(
        (element, index) =>
          slot.children[index] === element
      ) &&
      slot.children.length === content.length;

    if (!correctContent) {
      slot.replaceChildren(...content);
    }

    /*
     * Aplicamos estilos DESPUÉS de moverlo.
     * Si Synchrony vuelve a crear su Shadow DOM,
     * styleSynchrony tiene retries propios.
     */
    styleSynchrony(block);
  };

  /*
   * =========================================================
   * SHIPPING
   * =========================================================
   */

  const findShippingAccordion = (root) => {
    const details = [
      ...(root?.querySelectorAll(
        "details.product__accordion"
      ) || [])
    ];

    return details.find((element) => {
      const summary = element.querySelector(
        "summary.details__summary"
      );

      const heading = getText(summary);

      return /shipping policy|shipping/i.test(
        heading
      );
    }) || null;
  };

  const moveShippingToAccordion = (root) => {
    const shipping = root?.querySelector(
      ".product__price .product__tax"
    );

    const existingShipping =
      root?.querySelector(
        ".ig-shipping-message .product__tax"
      );

    const message =
      shipping || existingShipping;

    const accordion =
      findShippingAccordion(root);

    const content =
      accordion?.querySelector(
        ".details__content"
      );

    if (!message || !content) return;

    let wrapper =
      content.querySelector(
        ".ig-shipping-message"
      );

    if (!wrapper) {
      wrapper =
        document.createElement("div");

      wrapper.className =
        "ig-shipping-message";

      content.appendChild(wrapper);
    }

    if (message.parentElement !== wrapper) {
      wrapper.appendChild(message);
    }
  };

  /*
   * =========================================================
   * SYNC
   * =========================================================
   */

  const sync = () => {
    const root = getRoot();

    if (!root) return;

    const module = ensureModule(root);

    if (!module) return;

    updateShopPay(root, module);
    updateAfterpay(root, module);
    updateSynchrony(root, module);
    moveShippingToAccordion(root);

    /*
     * Si realmente no existe ningún método,
     * eliminamos el contenedor.
     *
     * Synchrony no se elimina mientras está cargando
     * si ya existe un slot/contenido.
     */
    if (!module.children.length) {
      module.remove();
    }
  };

  const scheduleSync = () => {
    window.clearTimeout(syncTimer);

    syncTimer = window.setTimeout(
      sync,
      100
    );
  };

  /*
   * =========================================================
   * INITIALIZE
   * =========================================================
   */

  const initialize = () => {
    const root = getRoot();

    /*
     * El product-info también puede tardar en aparecer.
     */
    if (!root) {
      window.setTimeout(
        initialize,
        250
      );

      return;
    }

    /*
     * Primer intento inmediato.
     */
    sync();

    /*
     * Seguimos cualquier cambio que hagan Shopify,
     * Afterpay, Synchrony o un cambio de variante.
     */
    observer = new MutationObserver(
      scheduleSync
    );

    observer.observe(root, {
      childList: true,
      subtree: true
    });

    /*
     * Revisiones adicionales para apps que terminan
     * de renderizar después del DOM inicial.
     *
     * IMPORTANTE PARA SYNCHRONY EN MOBILE.
     */
    window.setTimeout(sync, 500);
    window.setTimeout(sync, 1500);
    window.setTimeout(sync, 3000);
  };

  initialize();
})();